import { autorun } from 'mobx';
import { flow, getRoot } from 'mobx-state-tree';

import { logger } from '@lusha/client-logger';

import { fetchCommunitySettings, fetchCommunityStatus, fetchOutlookDesktopConnectionSettings } from '../../../API/communityAPI';
import { fromJsonToQueryString } from '../../../utils/queryBuilder';
import { COMMUNITY_STATUS, ELEMENTS_TYPES } from '../constants';

const Actions = self => ({
  afterCreate() {
    autorun(() => {
      const { user: { id, isLoaded } } = getRoot(self);

      if (id && isLoaded) {
        self.fetchData();
      }
    });
  },

  fetchData() {
    self.fetchCommunityData();
    self.fetchIsOutlookDesktopConnected();
  },

  setDisplayFirstShowBanner(setter) {
    self.displayFirstShowBanner = setter;
  },

  fetchCommunityData: flow(function* fetchCommunityData(query = {}) {
    try {
      self.isLoaded = false;

      const { billing, id, isAccountSuspended } = getRoot(self).user;
      if (isAccountSuspended || billing?.isPaid) {
        return Promise.resolve(COMMUNITY_STATUS.COMMUNITY_IRRELEVANT);
      }

      const communitySettings = yield fetchCommunitySettings(id);
      self.isComposedEnabled = communitySettings.isComposedEnabled;
      self.isCommunityTermsAccepted = communitySettings.isCommunityTermsAccepted;
      self.firstCommunityTabVisit = communitySettings?.analytics?.firstCommunityTabVisit;
      self.inviteNextAppearance = communitySettings?.analytics?.inviteNextAppearance;
      self.inviteDismissCount = communitySettings?.analytics?.inviteDismissCount || 0;
      self.reminderNextAppearance = communitySettings?.analytics?.reminderNextAppearance;
      self.reminderDismissCount = communitySettings?.analytics?.reminderDismissCount || 0;
      self.outlookExeDownloadDate = communitySettings?.analytics?.outlookExeDownloadDate || '';
      self.sourceValidationStatus = communitySettings?.sourceValidationStatus;

      const queryString = fromJsonToQueryString(query);
      const { status, segment } = yield fetchCommunityStatus(queryString);
      self.segment = segment || '';
      switch (status) {
        case COMMUNITY_STATUS.COMMUNITY_CANDIDATE:
        case COMMUNITY_STATUS.INACTIVE_COMMUNITY_MEMBER:
        case COMMUNITY_STATUS.LEFT_COMMUNITY:
        case COMMUNITY_STATUS.COMMUNITY_MEMBER:
        case COMMUNITY_STATUS.COMMUNITY_PENDING_JOIN_VALIDATION:
          self.status = status;
          break;
        case COMMUNITY_STATUS.COMMUNITY_IRRELEVANT:
        default:
          self.status = COMMUNITY_STATUS.COMMUNITY_IRRELEVANT;
          break;
      }

      if (query?.firstShow && self.status === COMMUNITY_STATUS.COMMUNITY_CANDIDATE) {
        self.displayFirstShowBanner = true;
      }

      self.isLoaded = true;

      return self.status;
    } catch (e) {
      logger.warn('Error fetching community status', e);
      self.isLoaded = true;
      self.status = COMMUNITY_STATUS.COMMUNITY_IRRELEVANT;

      return COMMUNITY_STATUS.COMMUNITY_IRRELEVANT;
    }
  }),

  fetchIsOutlookDesktopConnected: flow(function* fetchIsOutlookDesktopConnected() {
    try {
      self.isOutlookSettingsLoaded = false;
      const { isAccountSuspended, billing } = getRoot(self).user;
      if (isAccountSuspended || billing?.isPaid) { return; }

      const outlookSettings = yield fetchOutlookDesktopConnectionSettings();
      self.isOutlookDesktopConnected = outlookSettings?.data?.status === 'enabled';
      self.isOutlookSettingsLoaded = true;
    } catch (e) {
      self.isOutlookSettingsLoaded = true;
      logger.warn('Error fetching outlook settings', e);
    }
  }),

  updateElementReappearanceData(dismissCount, nextAppearanceDate, elementType) {
    if (elementType === ELEMENTS_TYPES.COMMUNITY_INVITE) {
      self.inviteNextAppearance = nextAppearanceDate;
      self.inviteDismissCount = dismissCount;
    } else {
      self.reminderNextAppearance = nextAppearanceDate;
      self.reminderDismissCount = dismissCount;
    }
  },

  setIsBannerDisplayed(value) {
    self.isBannerDisplayed = value;
  },
});

export default Actions;
